/**
 * BuildingLayoutPanel — native rewrite of the legacy BuildingLayout sub-tab
 * (shape picker + orientation + preview/OTHER-upload) and the walls wizard.
 *
 * The shape change runs `form.setShape` (prune-as-handler — NOT a mount effect),
 * and wall edits run `form.setWalls` (which applies the shape auto-compute). The
 * OTHER-shape building-shape upload is driven by the upload sync controller.
 */

import React from 'react'
import { Box, Fade, Grid } from '@mui/material'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'

import { GlobalShapeType, Wall } from 'constants/purchaseFlow'
import { BEIGE_500, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { certificateDocumentFileTypes } from 'constants/misc'
import { FileAPIUpload } from 'components/common/FileAPIUpload'
import { Label } from 'components/common/Label'
import { MUICheckbox } from 'components/common/MUICheckBox'
import { MUIFloatNumberField } from 'components/common/MUIFloatNumberField'
import { MUITooltip } from 'components/common/MUITooltip'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { SimpleImage } from 'components/common/SimpleImage'
import { ToggleButton } from 'components/common/ToggleButton'
import LShapeImg from 'assets/img/energy_certificates/L_shape.webp'
import TShapeImg from 'assets/img/energy_certificates/T_shape.webp'
import UShapeImg from 'assets/img/energy_certificates/U_shape.webp'
import rectangleImg from 'assets/img/energy_certificates/rectangle.webp'

import type { UseEnergyCertificateForm } from '../useEnergyCertificateForm'
import type { useEnergyUploadSync } from '../useEnergyUploadSync'
import { shapeWallSettings } from '../energyCertificateOfNeed.walls'
import { BuildingOrientation } from './BuildingOrientation'
import { NumericFloatField } from 'components/common/MUIFloatNumberField'

const shapeImage = (shape: GlobalShapeType): string => {
  switch (shape) {
    case GlobalShapeType.L_SHAPE:
      return LShapeImg
    case GlobalShapeType.U_SHAPE:
      return UShapeImg
    case GlobalShapeType.T_SHAPE:
      return TShapeImg
    default:
      return rectangleImg
  }
}

interface Props {
  form: UseEnergyCertificateForm
  uploads: ReturnType<typeof useEnergyUploadSync>
}

export const BuildingLayoutPanel: React.FC<Props> = ({ form, uploads }) => {
  const { t } = useTranslation(['purchase_flow'], { keyPrefix: 'energy_certificate_step.building_layout_page' })
  const { view, setShape, setOrientation, setWalls } = form
  const { buildingShape, uploadBuildingShape, deleteBuildingShape } = uploads

  const isOther = view.globalShapeType === GlobalShapeType.OTHER
  const walls = view.walls

  const updateWall = (type: Wall, partial: Partial<(typeof walls)[Wall]>) =>
    setWalls({ ...walls, [type]: { ...walls[type], ...partial } as NonNullable<(typeof walls)[Wall]> })

  return (
    <Stack>

      <SectionedBorderBox
        title={<Typography variant="text-md" fontWeight={600} color={GRAY_900}>{t('building_shape.building_shape_title')}</Typography>}
      >
        <Stack sx={{ flexDirection: { xs: 'column', lg: 'row' } }} spacing={2} width="100%">

          <Stack alignItems="flex-start" gap={1.2} width={{ xs: '100%', sm: '50%' }} marginBottom={{ xs: '2.8rem', sm: 0 }}>

            <Label color={GRAY_700} text={t('building_shape.global_shape_title')} />

            <Stack gap={0.8} flexDirection="row" flexWrap="wrap">
              {Object.values(GlobalShapeType).map((type) => (
                <ToggleButton
                  key={type}
                  isSelected={view.globalShapeType === type}
                  label={t(`building_shape.global_shape_type.${type}`)}
                  sx={{ width: '12rem', height: '4rem', justifyContent: 'center' }}
                  onClick={() => setShape(type)}
                  typographyVariant="text-md"
                />
              ))}
            </Stack>

            <Box marginTop={0.8}>
              <BuildingOrientation onClick={setOrientation} type={view.orientationType} />
            </Box>

          </Stack>

          {!isOther &&
            <Fade in={!isOther}>
              <Stack alignItems="flex-start" gap={2} width={{ xs: '100%', sm: '50%' }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '40.4rem',
                    height: '28rem',
                    backgroundColor: BEIGE_500,
                    borderRadius: '.8rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <SimpleImage url={shapeImage(view.globalShapeType)} sx={{ width: '75%', height: '75%' }} />
                </Box>
              </Stack>
            </Fade>
          }

          {isOther &&
            <Fade in={isOther}>
              <Stack alignItems="flex-start" gap={2} width={{ xs: '100%', sm: '50%' }}>
                <FileAPIUpload
                  title={t('upload_title')}
                  files={buildingShape.entries}
                  acceptedFileTypes={certificateDocumentFileTypes}
                  isDraggingFile={false}
                  uploadHandler={(acceptedFiles) => uploadBuildingShape(acceptedFiles)}
                  onDelete={(fileId) => deleteBuildingShape(fileId)}
                  onCancelUpload={(fileId) => deleteBuildingShape(fileId)}
                />
              </Stack>
            </Fade>
          }

        </Stack>
      </SectionedBorderBox>

      {!isOther &&
        <SectionedBorderBox
          title={<Typography variant="text-md" fontWeight={600} color={GRAY_900}>{t('wall_length.wall_length_title')}</Typography>}
          sx={{ marginTop: '1.6rem' }}
        >
          <Box flexGrow={1}>
            <Grid container spacing={2}>
              {Object.values(Wall)
                .filter((type) => Object.keys(walls).includes(type))
                .map((type) => {
                  const wall = walls[type]
                  const isDerived = shapeWallSettings[view.globalShapeType].some((setting) => setting.target === type)

                  return (
                    <Grid item xs={12} md={6} key={type}>
                      <SectionedBorderBox
                        title={<Typography variant="text-md" fontWeight={600} color={GRAY_900}>{t('wall_length.wall')}{type}</Typography>}
                      >
                        <Stack gap={0.8} alignItems="flex-start">

                          <Stack flexDirection="row" alignItems="center" gap=".8rem">
                            <MUICheckbox checked={wall?.mounted} onChange={(e) => updateWall(type, { mounted: e.target.checked })} />
                            <Typography variant="text-md" fontWeight="medium" color={GRAY_900}>{t('wall_length.mounted_wall')}</Typography>
                          </Stack>

                          <Stack flexDirection="row" alignItems="center" gap=".8rem">
                            <MUICheckbox checked={wall?.insulated} onChange={(e) => updateWall(type, { insulated: e.target.checked })} />
                            <Typography variant="text-md" fontWeight="medium" color={GRAY_900}>{t('wall_length.subsequently_insulated')}</Typography>
                          </Stack>

                          <Stack flexDirection="row" alignItems="center" gap=".8rem" marginTop={0.8}>

                            <Box width={{ xs: '80%', lg: '45%' }}>
                              {isDerived
                                ? <MUIFloatNumberField
                                    disabled
                                    suffix="M"
                                    inputBoxWidth="100%"
                                    label={t('wall_length.length')}
                                    value={wall?.length}
                                  />
                                : <NumericFloatField
                                    required
                                    suffix="M"
                                    inputBoxWidth="100%"
                                    label={t('wall_length.length')}
                                    tooltip={
                                      <MUITooltip content={t('wall_length.length_tooltip')}>
                                        <InfoOutlinedIcon fontSize="medium" sx={{ paddingTop: '.4rem' }} />
                                      </MUITooltip>
                                    }
                                    value={wall?.length ?? ''}
                                    onCommit={(value) => updateWall(type, { length: value })}
                                  />
                              }
                            </Box>

                            <Box width={{ xs: '80%', lg: '45%' }}>
                              <NumericFloatField
                                required={wall?.insulated}
                                disabled={!wall?.insulated}
                                suffix="Cm"
                                inputBoxWidth="100%"
                                label={t('wall_length.insulation')}
                                value={wall?.insulation ?? ''}
                                onCommit={(value) => updateWall(type, { insulation: value })}
                              />
                            </Box>

                          </Stack>

                        </Stack>
                      </SectionedBorderBox>
                    </Grid>
                  )
                })}
            </Grid>
          </Box>
        </SectionedBorderBox>
      }

    </Stack>
  )
}
